// Session/SpecialSessionFormBuilder.tsx

import { useMemo } from 'react';
import { Center, Loader } from '@mantine/core';
import { useSessionCustomFieldService } from '@actschurch/ccms-sdk';

import {
  FormBuilder,
  createCustomFieldAdapter,
  useFieldTypeCatalogue,
} from '@/components/advanced/FormBuilder';

// ---------------------------------------------------------------------------
// Session custom form page
// ---------------------------------------------------------------------------

export interface SessionFormPageProps {
  courseSessionID: number;
  /**
   * The owning course. ⚠️ Not read here — it is kept because `SessionTab` passes it, and it was the
   * only thing the old (broken) `onDone` used. Left on the props so the call sites need no change.
   */
  courseID: number;
  /** Called when the builder's Done button is pressed. Usually closes the hosting modal. */
  onDone?: () => void;
}

export function SessionFormPage({ courseSessionID, onDone }: SessionFormPageProps) {
  const sessionCustomField = useSessionCustomFieldService();
  const fieldTypes = useFieldTypeCatalogue();

  const adapter = useMemo(() => {
    if (!fieldTypes) return null;
    return createCustomFieldAdapter({
      fieldTypes,
      list: () => sessionCustomField.byCourseSession(courseSessionID),
      create: (data) => sessionCustomField.create({ ...data, courseSessionID }),
      update: (id, data) => sessionCustomField.update(id, data),
      remove: (id) => sessionCustomField.delete(id),
    });
  }, [fieldTypes, courseSessionID, sessionCustomField]);

  if (!adapter || !fieldTypes) {
    return (
      <Center py="xl">
        <Loader />
      </Center>
    );
  }

  return (
    <FormBuilder
      title="Session Custom Form"
      subtitle="Applies to all registrations for this Course"
      adapter={adapter}
      formTypes={fieldTypes}
      onDone={onDone}
    />
  );
}

// ---------------------------------------------------------------------------
// Read-only viewer — same FormBuilder component, same adapter shape, just
// `readonly` so there's no field palette, no edit/delete/move controls, and
// no Save button. Only needs `list`, since nothing here ever writes.
// ---------------------------------------------------------------------------

export interface SessionFormViewerProps {
  courseSessionID: number;
}

export function SessionFormViewer({ courseSessionID }: SessionFormViewerProps) {
  const sessionCustomField = useSessionCustomFieldService();
  const fieldTypes = useFieldTypeCatalogue();

  const adapter = useMemo(() => {
    if (!fieldTypes) return null;
    return createCustomFieldAdapter({
      fieldTypes,
      list: () => sessionCustomField.byCourseSession(courseSessionID),
    });
  }, [fieldTypes, courseSessionID, sessionCustomField]);

  if (!adapter || !fieldTypes) {
    return (
      <Center py="xl">
        <Loader />
      </Center>
    );
  }

  return (
    <FormBuilder
      title="Session Custom Form"
      subtitle="Read-only view"
      adapter={adapter}
      formTypes={fieldTypes}
      readonly
    />
  );
}


export default SessionFormPage;